// Needs you on the phone (Poulpe-624), as the approved prototype drew it:
// the logo, then one list in Fabien's order (needs.ts), each row the board's
// card; empty, "Nothing needs you" and the way to the digest. A question
// whose answer is on its way from this phone says so, and is not counted.
import { Link } from 'react-router-dom'
import { BoardLogo, PriorityIcon } from '../components/Icons'
import { short } from '../queue/words'
import { QuestionFlags, QuestionRevised, useExpiryClock, waits } from '../questions/waiting'
import { useBoardState } from '../store/context'
import { useKeeper } from './answers'
import { needItems, waitingCount, type NeedItem } from './needs'
import { askerName, clock, headline, Screen, TopBar } from './shared'

/** What a row and a screen name an item by. */
export function itemTitle(item: NeedItem): string {
  if (item.kind === 'question') return headline(item.question)
  const keys = item.issues.map((issue) => issue.key).join(', ')
  if (item.kind === 'held') return keys ? `The merge of ${keys} stopped` : 'A merge stopped'
  return keys ? `Approve the landing of ${keys}` : 'Approve a landing'
}

/** What needs the person, and how many wait for them: for the list and the tab bar's badge. */
export function useNeedItems(): { items: NeedItem[]; count: number; now: number } {
  const state = useBoardState()
  const { kept } = useKeeper()
  const now = useExpiryClock([...state.questions.values()])
  const items = needItems(state, now, kept)
  return { items, count: waitingCount(items), now }
}

export function NeedsYou() {
  const { items, count, now } = useNeedItems()
  return (
    <Screen top={<TopBar title="Needs you" back={null} brand />} tabs={{ current: 'needs', count }}>
      {items.length === 0 ? (
        <div className="state-message m-empty">
          <BoardLogo size={40} />
          <h2>Nothing needs you</h2>
          <p className="muted">Questions and approvals that wait for you appear here.</p>
          <Link className="m-target" to="/digest">What happened in the last 24 hours</Link>
        </div>
      ) : (
        <ul className="m-items" aria-label={`${count} waiting for you`}>
          {items.map((item) => <li key={item.id}><ItemRow item={item} now={now} /></li>)}
        </ul>
      )}
    </Screen>
  )
}

/** A row: the board's card (board/Card.tsx), what needs the person as its title. */
function ItemRow({ item, now }: { item: NeedItem; now: number }) {
  const href = item.kind === 'question' ? `/q/${item.id}` : `/approval/${item.id}`
  const issue = item.kind === 'question' ? item.issue : item.issues[0]
  const kind = item.kind === 'question' ? 'Question' : item.kind === 'held' ? 'Stopped' : 'Approval'
  return (
    <Link className="card m-card" data-priority={issue?.priority ?? 'none'} to={href}>
      <span className="card-top">
        {issue && <PriorityIcon priority={issue.priority} />}
        {issue && <span className="card-key">{issue.key}</span>}
        <span className="kind-chip">{kind}</span>
        {item.project && <span className="request-project">{item.project.name}</span>}
      </span>
      <span className="card-title">{itemTitle(item)}</span>
      <span className="m-card-foot">
        {item.kind === 'question' && item.going ? (
          <span className="muted" role="status">{item.going === 'kept' ? 'Your answer is kept on this phone, to go once it is back online' : 'Sending your answer…'}</span>
        ) : item.kind === 'question' ? (
          <>
            <QuestionFlags question={item.question} now={now} />
            {/* The flag's hover says until when; a phone has no hover, so it is said. */}
            {item.question.expiry && waits(item.question, now) && <span className="muted">until {clock(item.question.expiry.at)}</span>}
            <QuestionRevised question={item.question} now={now} first={!item.question.urgent && !item.question.blocking} />
            <span className="muted">{askerName(item.session)}</span>
          </>
        ) : item.kind === 'held' ? (
          <span className="muted">{item.why}</span>
        ) : (
          <span className="muted">Commit <code>{short(item.asked.subject.commit)}</code></span>
        )}
      </span>
    </Link>
  )
}
